import { useRouter } from '@tanstack/react-router'
import type { Workspace } from '@botpress/client'
import { useMemo, useState, useRef, type ReactNode, useCallback } from 'react'
import { DYNAMIC_QUOTAS, QuotaUsedPercentageWarningThreshold } from '~/features/usage/constants'
import { WorkspaceProfilePicture } from '~/features/workspaces/components'
import { useUsage } from '~/hooks'
import { useAppStore } from '~/stores'
import Fuse from 'fuse.js'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  showDialog,
  useSidebar,
} from '@bpinternal/ui-kit-next'
import { CircleAlert, Plus, Search, TriangleAlert } from 'lucide-react'
import { useDebounce } from 'react-use'
import { Boundary } from '~/components/Boundary'
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog'
import { flushSync } from 'react-dom'
import { isLegacyBilling } from '~/features/billing/utils/billingVersion'

type Props = { className?: string; workspaces: Workspace[]; children: ReactNode; asChild?: boolean }

export const WorkspacesDropdown = ({ workspaces, children, asChild = true }: Props) => {
  const activeWorkspace = useAppStore((s) => s.activeWorkspace)
  const navigate = useRouter().navigate
  const [open, setOpen] = useState(false) // Need to controle the state for now because of a bug in radix, see: https://github.com/radix-ui/primitives/issues/837#issuecomment-2716644471
  const { isMobile, setOpenMobile } = useSidebar()

  const handleWorkspaceSelection = (workspaceId: string) => {
    if (isMobile) {
      setOpenMobile(false)
    }
    navigate({ to: '/workspaces/$workspaceId', params: { workspaceId } })
  }

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  const filteredWorkspaces = useMemo(() => {
    if (!debouncedQuery.trim()) return workspaces || []

    const fuse = new Fuse(workspaces || [], {
      keys: ['name', 'id'],
      threshold: 0.3,
      includeScore: true,
    })

    const results = fuse.search(debouncedQuery)
    return results.map((result) => result.item)
  }, [workspaces, debouncedQuery])

  const sortedWorkspaces = filteredWorkspaces
    ? [...filteredWorkspaces].sort((a, b) => a.name.localeCompare(b.name))
    : []

  const totalItems = sortedWorkspaces.length + 1

  useDebounce(
    () => {
      setDebouncedQuery(searchQuery)
      setSelectedIndex(null)
    },
    100,
    [searchQuery]
  )

  const handleKeyboardNavigation = (key: string) => {
    if (key === 'ArrowDown') {
      const newIndex = selectedIndex === null ? 0 : Math.min(selectedIndex + 1, totalItems - 1)
      flushSync(() => setSelectedIndex(newIndex))
      itemRefs.current[newIndex ?? 0]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    } else if (key === 'ArrowUp') {
      const newIndex = selectedIndex === null ? totalItems - 1 : Math.max(selectedIndex - 1, 0)
      flushSync(() => setSelectedIndex(newIndex))
      itemRefs.current[newIndex ?? 0]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    } else if (key === 'Enter' && selectedIndex !== null) {
      if (selectedIndex < sortedWorkspaces.length) {
        const workspace = sortedWorkspaces[selectedIndex]
        if (!workspace || workspace.id === activeWorkspace?.id) return

        handleOpenChange(false)
        handleWorkspaceSelection(workspace.id)
      } else {
        handleOpenChange(false)
        showDialog(({ close }) => <CreateWorkspaceDialog close={close} />, { title: 'Create a workspace' })
      }
    }
  }

  // useCallback ensures the function reference stays stable between renders.
  // This prevents unnecessary re-renders in Radix internals (which rely on strict prop identity checks) and avoids re-triggering effects or event listeners that depend on this callback.
  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setSearchQuery('')
      setDebouncedQuery('')
      setSelectedIndex(null)
    }
  }, [])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild={asChild}>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="flex w-60 flex-col"
        style={isMobile ? undefined : { maxHeight: 'min(50vh, var(--radix-dropdown-menu-content-available-height))' }}
        onAnimationEnd={() => {
          if (!open) {
            setSearchQuery('')
            setDebouncedQuery('')
            setSelectedIndex(null)
          }
        }}
      >
        <div className="relative">
          <Search className="absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || (e.key === 'Enter' && selectedIndex !== null)) {
                e.preventDefault()
                e.stopPropagation()
                handleKeyboardNavigation(e.key)
              } else {
                e.stopPropagation()
              }
            }}
            className="h-8 border-0 bg-transparent pl-8 shadow-none focus:border-0 focus:ring-0 focus-visible:ring-0"
            autoFocus
          />
        </div>
        <DropdownMenuSeparator />
        <div className="flex-1 overflow-y-auto">
          {sortedWorkspaces.map((workspace, index) => {
            const isSelected = selectedIndex === index
            return (
              <DropdownMenuItem
                key={workspace.id}
                ref={(el) => {
                  itemRefs.current[index] = el
                }}
                disabled={workspace.id === activeWorkspace?.id}
                onClick={() => handleWorkspaceSelection(workspace.id)}
                className={isSelected ? 'bg-accent' : ''}
              >
                <WorkspaceProfilePicture
                  workspaceId={workspace.id}
                  className="h-5 w-5 rounded-full"
                  pictureUrl={workspace.profilePicture}
                />
                <span className="truncate">{workspace.name}</span>
                <Boundary loader={null} onError={() => null}>
                  <WorkspaceUsageIcon workspaceId={workspace.id} billingVersion={workspace.billingVersion} />
                </Boundary>
              </DropdownMenuItem>
            )
          })}
        </div>
        <div className="sticky bottom-0 bg-popover">
          <DropdownMenuSeparator />
          <DropdownMenuItem
            ref={(el) => {
              itemRefs.current[sortedWorkspaces.length] = el
            }}
            onClick={() => {
              handleOpenChange(false)
              showDialog(({ close }) => <CreateWorkspaceDialog close={close} />, { title: 'Create a workspace' })
            }}
            className={selectedIndex === sortedWorkspaces.length ? 'bg-accent' : ''}
          >
            <Plus />
            <span>Create a workspace</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const WorkspaceUsageIcon = ({ workspaceId, billingVersion }: { workspaceId: string; billingVersion: string }) => {
  if (!isLegacyBilling({ billingVersion })) {
    return null
  }

  return <WorkspaceUsageIconContent workspaceId={workspaceId} />
}

const WorkspaceUsageIconContent = ({ workspaceId }: { workspaceId: string }) => {
  const workspaceUsage = useUsage({ workspaceId, quotas: DYNAMIC_QUOTAS })
  const values = Object.values(workspaceUsage)
  const anyUsageHasReachedQuota = values.some((usage) => usage.percentage >= 100)
  const anyUsageExceedsWarningThreshold = !anyUsageHasReachedQuota
    ? values.some((usage) => usage.percentage > QuotaUsedPercentageWarningThreshold)
    : true
  if (anyUsageHasReachedQuota) return <CircleAlert className="ml-auto text-red-500" />
  if (anyUsageExceedsWarningThreshold) return <TriangleAlert className="ml-auto text-amber-500" />
  return null
}
